import type { PairingAnalysisData } from '@/common/db-typebox/pairing.typebox';
import type { WineProductSearchResult } from '@/common/domain-model';
import type {
  WineDishRecommendation,
  WineRecommendation,
} from '@/common/generated/wine-dish-recommendation.typebox';
import { ImageUpload } from '@/components/ImageUpload';
import { useTreatyClient } from '@/lib/client';
import { Button, Modal, message } from 'antd';
import { type FC, useState } from 'react';
import { usePairingStore } from '../store/PairingContext';

interface EditWineImageModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (newImageUrl: string) => void;
  productId: string;
  currentImageUrl?: string;
}

const EditWineImageModal: FC<EditWineImageModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  productId,
  currentImageUrl,
}) => {
  const [uploading, setUploading] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState<string | undefined>(
    currentImageUrl,
  );
  const client = useTreatyClient();
  const { currentSelection, setCurrentSelection } = usePairingStore();

  const updateAnalysisData = (
    analysisData: PairingAnalysisData,
  ): PairingAnalysisData => {
    const updatedRecommendations =
      analysisData.wine_recommendation?.recommendations?.map<WineRecommendation>(
        (recommendation: WineRecommendation) => {
          if (recommendation.productId === productId) {
            return {
              ...recommendation,
              productImageUrl: newImageUrl,
            };
          }
          return recommendation;
        },
      ) || [];

    const updatedProducts =
      analysisData.products?.map<WineProductSearchResult>(
        (product: WineProductSearchResult) => {
          if (product.id === productId) {
            return {
              ...product,
              data: {
                ...product.data,
                image_urls: [newImageUrl, ...(product.data.image_urls || [])],
              },
            } as WineProductSearchResult;
          }
          return product;
        },
      ) || [];

    return {
      ...analysisData,
      wine_recommendation: {
        ...analysisData.wine_recommendation,
        recommendations: updatedRecommendations,
      } as WineDishRecommendation,
      products: updatedProducts,
    };
  };

  const handleSave = async () => {
    if (!newImageUrl) {
      message.warning('Please upload an image first');
      return;
    }

    setUploading(true);
    try {
      const response = await client.api.pairing
        .wine({
          id: productId,
        })
        ['update-image'].post({
          image_url: newImageUrl,
        });

      if (response.data?.success) {
        // Update the current selection in the store
        if (currentSelection) {
          if (
            currentSelection.type === 'pairing' &&
            currentSelection.pairing.analysis_data
          ) {
            const analysisData = currentSelection.pairing.analysis_data;
            const updatedAnalysisData = updateAnalysisData(analysisData);
            if (updatedAnalysisData) {
              setCurrentSelection({
                ...currentSelection,
                pairing: {
                  ...currentSelection.pairing,
                  analysis_data: updatedAnalysisData,
                },
              });
            }
          } else if (
            currentSelection.type === 'menu' &&
            currentSelection.menu.pairing?.analysis_data
          ) {
            const updatedAnalysisData = updateAnalysisData(
              currentSelection.menu.pairing.analysis_data,
            );
            if (updatedAnalysisData) {
              setCurrentSelection({
                ...currentSelection,
                menu: {
                  ...currentSelection.menu,
                  pairing: {
                    ...currentSelection.menu.pairing,
                    analysis_data: updatedAnalysisData,
                  },
                },
              });
            }
          }
        }

        message.success('Image updated successfully');
        onSuccess(newImageUrl);
      } else {
        message.error(
          `Update failed: ${response.error?.value?.message || 'Unknown error'}`,
        );
      }
    } catch (error) {
      message.error(
        `Update failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setNewImageUrl(currentImageUrl);
    onCancel();
  };

  return (
    <Modal
      title="Edit Wine Image"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          disabled={!newImageUrl || uploading}
          loading={uploading}
        >
          {uploading ? 'Saving...' : 'Save Changes'}
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-4">
        <div className="flex justify-center">
          {newImageUrl && (
            <img
              src={newImageUrl}
              alt="Wine"
              className="max-w-[240px] max-h-[320px] object-contain"
            />
          )}
        </div>
        <ImageUpload
          value={newImageUrl}
          onSuccess={(url) => setNewImageUrl(url)}
        />
      </div>
    </Modal>
  );
};

export default EditWineImageModal;
